
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Edit, Trash2, Navigation } from "lucide-react";
import { ClientStatus } from "@/types/clientStatus";
import { useDeleteClientStatus, useUpdateClientStatus } from "@/hooks/useClientStatuses";

interface ClientStatusTableProps {
  statuses: ClientStatus[];
  onEdit: (status: ClientStatus) => void;
}

export const ClientStatusTable = ({ statuses, onEdit }: ClientStatusTableProps) => {
  const deleteMutation = useDeleteClientStatus();
  const updateMutation = useUpdateClientStatus();

  const handleDelete = async (status: ClientStatus) => {
    if (confirm(`هل أنت متأكد من حذف الحالة "${status.name_ar}"؟`)) {
      try {
        await deleteMutation.mutateAsync(status.id);
      } catch (error) {
        console.error("Error deleting status:", error);
      }
    }
  };

  const handleToggleActive = async (status: ClientStatus, isActive: boolean) => {
    try {
      await updateMutation.mutateAsync({
        id: status.id,
        is_active: isActive
      });
    } catch (error) {
      console.error("Error toggling status:", error);
    }
  };

  const handleToggleTopNav = async (status: ClientStatus, showInTopNav: boolean) => {
    try {
      console.log(`Toggling top nav visibility for status: ${status.name_ar}, setting to: ${showInTopNav}`);
      await updateMutation.mutateAsync({
        id: status.id,
        show_in_top_nav: showInTopNav
      });
      console.log(`Successfully toggled top nav visibility for status: ${status.name_ar}`);
    } catch (error) {
      console.error("Error toggling top nav visibility:", error);
    }
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-right">الترتيب</TableHead>
            <TableHead className="text-right">الاسم بالعربية</TableHead>
            <TableHead className="text-right">الاسم بالإنجليزية</TableHead>
            <TableHead className="text-right">المفتاح</TableHead>
            <TableHead className="text-right">اللون</TableHead>
            <TableHead className="text-right">معاينة</TableHead>
            <TableHead className="text-right">نشط</TableHead>
            <TableHead className="text-right">ظاهر بالقائمة العلوية</TableHead>
            <TableHead className="text-right">الإجراءات</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {statuses.map((status) => (
            <TableRow key={status.id} className={!status.is_active ? 'opacity-60' : ''}>
              <TableCell className="font-medium">{status.sort_order}</TableCell>
              <TableCell className="font-medium">{status.name_ar}</TableCell>
              <TableCell>{status.name_en}</TableCell>
              <TableCell>
                <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                  {status.status_key}
                </code>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-6 h-6 rounded border"
                    style={{ backgroundColor: status.color }}
                  />
                  <span className="text-xs text-gray-600">{status.color}</span>
                </div>
              </TableCell>
              <TableCell>
                <Badge 
                  className="text-white text-xs"
                  style={{ backgroundColor: status.color }}
                >
                  {status.name_ar}
                </Badge>
              </TableCell>
              <TableCell>
                <Switch
                  checked={status.is_active}
                  onCheckedChange={(checked) => handleToggleActive(status, checked)}
                />
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={status.show_in_top_nav}
                    onCheckedChange={(checked) => handleToggleTopNav(status, checked)}
                  />
                  <Navigation className={`w-4 h-4 ${status.show_in_top_nav ? 'text-blue-500' : 'text-gray-400'}`} />
                  <span className="text-xs text-gray-600">
                    {status.show_in_top_nav ? 'ظاهر' : 'مخفي'}
                  </span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(status)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(status)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
